import {useState} from 'react'
import axios from 'axios'
import {getAuth} from '../../../../app/modules/auth/core/AuthHelpers'

const API_URL = process.env.REACT_APP_THEME_API_URL

// Estado local por cobrança enquanto o usuário interage com os botões "Já resolvi" / "Ignorar".
export interface NudgeActionState {
  loading?: boolean
  message?: string
  removed?: boolean
}

interface UseNudgeActionsOptions {
  onResolved?: (id: number) => void
  onDismissed?: (id: number) => void
}

// Garante o Bearer token nas chamadas de cobrança (mesmo padrão do widget RushMind).
const applyAuthHeader = () => {
  const auth = getAuth()
  if (auth?.api_token) {
    ;(axios.defaults.headers as any).Authorization = `Bearer ${auth.api_token}`
  }
}

/**
 * Lógica compartilhada dos botões de cobrança (nudges) do RushMind: "Já resolvi"
 * chama /check (reconcilia contra o estado atual da tarefa) e "Ignorar" chama
 * /dismiss. Usado tanto pelo widget quanto pela seção RushMind da tela de desempenho.
 */
export function useNudgeActions(options: UseNudgeActionsOptions = {}) {
  const [state, setState] = useState<Record<number, NudgeActionState>>({})

  const reset = () => setState({})

  const setLoading = (id: number, loading: boolean) =>
    setState((prev) => ({...prev, [id]: {...prev[id], loading, message: undefined}}))

  const setMessage = (id: number, message: string) =>
    setState((prev) => ({...prev, [id]: {...prev[id], loading: false, message}}))

  const markRemoved = (id: number) => setState((prev) => ({...prev, [id]: {removed: true}}))

  const check = async (id: number) => {
    if (state[id]?.loading) {
      return
    }
    setLoading(id, true)
    try {
      applyAuthHeader()
      const response = await axios.post(`${API_URL}/api/rushmind/nudges/${id}/check`)
      if (response.data?.resolved) {
        markRemoved(id)
        options.onResolved?.(id)
      } else {
        setMessage(
          id,
          response.data?.message ||
            'Ainda não consegui confirmar. Atualize a tarefa e tente de novo.'
        )
      }
    } catch {
      setMessage(id, 'Não foi possível concluir agora. Tente novamente mais tarde.')
    }
  }

  const dismiss = async (id: number) => {
    if (state[id]?.loading) {
      return
    }
    setLoading(id, true)
    try {
      applyAuthHeader()
      await axios.post(`${API_URL}/api/rushmind/nudges/${id}/dismiss`, {})
      markRemoved(id)
      options.onDismissed?.(id)
    } catch {
      setMessage(id, 'Não foi possível concluir agora. Tente novamente mais tarde.')
    }
  }

  return {state, check, dismiss, reset}
}
